import { observer } from 'mobx-react-lite';
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { IMediaPermissions } from '@gem/call/CallStore/Call.types';
import {
  Avatar,
  AvatarTypes,
  TAvatarBackgroundColorIndex,
} from '@gem/call/Removable/components/Avatar/Avatar';
import { ActiveSpeakerIcon } from '@gem/call/components/ActiveSpeakerIcon/ActiveSpeakerIcon';
import { BlurButton } from '@gem/call/components/RoomCallPreviewVideo/BlurButton/BlurButton';
import { useRoomCallBackgroundVideoPresenter } from '@gem/call/components/RoomCallPreviewVideo/RoomCallPreviewVideo.background.presenter';
import { CallBlurType } from '@gem/call/constants';
import { IDevices } from '@gem/call/tasks/getDefaultDevices';
import { IcMicOff16Icon } from '@gem/icons';
import { IconView, IconViewSizes } from '@gem/ui-kit';

import { AnimatedBackgroundPanel } from './AnimatedBackgroundPanel/AnimatedBackgroundPanel';
import { PreviewActionsPanel } from './PreviewActionsPanel/PreviewActionsPanel';
import { PreviewVideo } from './PreviewVideo/PreviewVideo';
import { useRoomCallAudioSourcePreviewVideoPresenter } from './RoomCallPreviewVideo.audioSource.presenter';
import { useRoomCallPreviewVideoPresenter } from './RoomCallPreviewVideo.presenter';
import styles from './RoomCallPreviewVideo.styles.m.css';

interface IRoomCallPreviewVideoProps {
  onChangeAudioMuted(audioMute: boolean): void;
  onChangeVideoMuted(videoMute: boolean): void;
  onMediaPermissionsChanged?(params: Partial<IMediaPermissions>): void;
  onChangeInitialDevicesIds?(props: IDevices): void;
  avatar: {
    title: string | null;
    url: string | null;
    colorIndex: TAvatarBackgroundColorIndex | null;
  };
  initialMedia?: {
    isVideoMuted: boolean;
    isAudioMuted: boolean;
  };
  backgroundPanelContainer: HTMLDivElement | null;
}

export const RoomCallPreviewVideo = observer(
  forwardRef<() => Promise<void>, IRoomCallPreviewVideoProps>((props, ref) => {
    const videoBackgroundPresenter = useRoomCallBackgroundVideoPresenter();
    const audioSourcePresenter = useRoomCallAudioSourcePreviewVideoPresenter();
    const presenter = useRoomCallPreviewVideoPresenter({
      ...props,
      audioSourcePresenter,
      videoBackgroundPresenter,
    });

    useImperativeHandle(
      ref,
      () => {
        return presenter.onCallStart;
      },
      []
    );

    const currentStream = useMemo(() => {
      return videoBackgroundPresenter.blurType === CallBlurType.None
        ? presenter.videoStream
        : videoBackgroundPresenter.backgroundVideoStream;
    }, [
      videoBackgroundPresenter.blurType,
      presenter.videoStream,
      videoBackgroundPresenter.backgroundVideoStream,
    ]);

    const onBlurClick = useCallback(
      (blurType: CallBlurType): void => {
        videoBackgroundPresenter.setBackground(blurType, presenter.videoStream);
      },
      [presenter.videoStream]
    );

    return (
      <div className={styles.container}>
        <div className={styles.metaGroup}>
          <div className={styles.name}>{props.avatar.title}</div>
          {presenter.audioMuted ? (
            <IconView
              Icon={IcMicOff16Icon}
              size={IconViewSizes.Small}
              className={styles.muted}
            />
          ) : (
            <ActiveSpeakerIcon
              isBig
              isSilence={!audioSourcePresenter.isSpeaking}
            />
          )}
        </div>
        <BlurButton
          isBackgroundPanelShown={presenter.isBackgroundPanelShown}
          isDisabled={!videoBackgroundPresenter.canShowBackgroundPanel}
          onClick={
            presenter.isBackgroundPanelShown
              ? presenter.hideBackgroundPanel
              : presenter.showBackgroundPanel
          }
        />
        {!presenter.videoMuted && currentStream && (
          <PreviewVideo
            videoStream={currentStream}
            blurType={videoBackgroundPresenter.blurType}
          />
        )}
        <div className={styles.content}>
          {presenter.videoMuted && (
            <Avatar
              type={AvatarTypes.Round_30}
              className={styles.avatar}
              title={props.avatar.title}
              backgroundColorIndex={props.avatar.colorIndex}
              url={props.avatar.url}
            />
          )}
          <PreviewActionsPanel
            canShowBackgroundPanel={videoBackgroundPresenter.canShowBackgroundPanel}
            videoPermissionDisabled={!presenter.hasVideoPermission}
            audioPermissionDisabled={!presenter.hasAudioPermission}
            showBackgroundPanel={presenter.showBackgroundPanel}
            isAudioMuted={presenter.audioMuted}
            isVideoMuted={presenter.videoMuted}
            unmuteAudio={presenter.unmuteAudio}
            muteAudio={presenter.muteAudio}
            unmuteVideo={presenter.unmuteVideo}
            muteVideo={presenter.muteVideo}
            setCurrentAudioInput={presenter.setCurrentAudioInput}
            setCurrentVideoInput={presenter.setCurrentVideoInput}
            setCurrentAudioOutput={presenter.setCurrentAudioOutput}
            audioInputDeviceId={presenter.currentAudioInput}
            audioOutputDeviceId={presenter.currentAudioOutput}
            videoInputDeviceId={presenter.currentVideoInput}
          />
        </div>
        {props.backgroundPanelContainer &&
          videoBackgroundPresenter.canShowBackgroundPanel &&
          createPortal(
            <AnimatedBackgroundPanel
              isPanelShown={presenter.isBackgroundPanelShown}
              hasVideo={!presenter.videoMuted}
              mediaStream={currentStream}
              blurType={videoBackgroundPresenter.blurType}
              onBlurClick={onBlurClick}
              hidePanel={presenter.hideBackgroundPanel}
            />,
            props.backgroundPanelContainer
          )}
      </div>
    );
  })
);
